import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";

    const [form, setForm] = useState({
        full_name: "",
        email: "",
        password: "",
        organization: "",
        role: "faculty",
    });
    const [busy, setBusy] = useState(false);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        try {
            setBusy(true);
            await register(form);
            toast.success("Account created");
            navigate(next);
        } catch (err) {
            toast.error("Could not register", { description: err?.response?.data?.detail || err.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid lg:grid-cols-2">
            <div className="hidden lg:block bg-black text-white p-16 relative">
                <div className="grid-line opacity-10 absolute inset-0" />
                <div className="relative h-full flex flex-col justify-between">
                    <p className="overline text-white/70">Faculty access</p>
                    <div>
                        <h2 className="font-heading font-black text-5xl tracking-tighter leading-[0.95]">
                            Create your<br />
                            <span className="text-klein bg-white px-2">faculty account.</span>
                        </h2>
                        <p className="mt-6 text-white/70 max-w-md">
                            Free to register. Pay only for the modules you want.
                        </p>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center px-6 py-16">
                <form onSubmit={submit} data-testid="register-form" className="w-full max-w-md">
                    <h1 className="font-heading font-black text-4xl tracking-tighter">Get started</h1>
                    <p className="text-muted-foreground mt-2">Takes 30 seconds.</p>

                    <div className="mt-8 space-y-5">
                        <Input label="Full name" testId="reg-name" value={form.full_name} onChange={onChange("full_name")} required />
                        <Input label="Email" type="email" testId="reg-email" value={form.email} onChange={onChange("email")} required />
                        <Input label="Password (min 6)" type="password" testId="reg-password" value={form.password} onChange={onChange("password")} required minLength={6} />
                        <Input label="Institution / school" testId="reg-org" value={form.organization} onChange={onChange("organization")} />

                        <button
                            type="submit"
                            data-testid="register-submit"
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50"
                        >
                            {busy ? "Creating..." : "Create account"} <ArrowRight size={16} />
                        </button>
                    </div>

                    <p className="mt-6 text-sm text-muted-foreground">
                        Already have an account?{" "}
                        <Link to="/login" className="text-klein font-semibold underline-offset-4 hover:underline" data-testid="goto-login">
                            Sign in
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
}

function Input({ label, testId, ...rest }) {
    return (
        <label className="flex flex-col gap-2">
            <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
            <input
                data-testid={testId}
                {...rest}
                className="w-full bg-white border border-black/20 px-3 py-2.5 text-sm focus:outline-none focus:border-klein focus:ring-2 focus:ring-klein/20"
            />
        </label>
    );
}
